import { layerClass } from "@freightsimple/ui-core/Theme/layers";
import SlideDown from "@/Shared/Animations/CustomComponents/SlideDown";
import { useHeight } from "@/Shared/Hooks/useHeight";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { useState } from "react";
import { animated, config, useSpring } from "@react-spring/web";

/**
 * Break glass — the expandable quote card on the quotes and book-shipment
 * screens.
 *
 * ui-core's nearest relative is `Panel collapsible`, and it isn't a substitute:
 * Panel owns its header (title, stats, actions, caret), tells the header nothing
 * about the open state, and toggles with a Radix Collapsible. This card hands
 * `header`/`body` the open flag so a quote row can restyle itself as it expands,
 * makes the whole collapsed strip the click target rather than just a caret, and
 * springs between a *measured* collapsed height and a *measured* content height
 * via react-spring — every `style` below is one of those runtime values or the
 * chevron's animated flip transform, none of which is a Tailwind class.
 */
interface RevealBoxProps {
  header: (open: boolean) => ReactNode;
  body: (open: boolean) => ReactNode;
  /** Rendered right-aligned under the body (e.g. a download-quote link). */
  footer?: ReactNode;
}

// Animates the card between the collapsed height (the measured header row —
// 96px on one line, taller when the header wraps on narrow screens) and the
// measured height of the full content.
function AnimatedContainer_(props: {
  open: boolean;
  collapsedHeight: number;
  children: ReactNode;
}): ReactElement {
  const [heightRef, height] = useHeight();
  const slideInStyles = useSpring({
    // clamp stops the spring dead at the target — without it the stiff
    // config overshoots on close and the card visibly bounces.
    config: { ...config.stiff, clamp: true },
    from: { height: props.collapsedHeight + "px" },
    to: {
      height: props.open ? height + 8 + "px" : props.collapsedHeight + "px",
    },
  });
  return (
    <animated.div style={slideInStyles} className="w-full overflow-hidden">
      <div ref={heightRef}>{props.children}</div>
    </animated.div>
  );
}

// The chevron artwork points down; this pre-flip makes it point up when
// closed, and the spring in RevealButton_ flips it back down when open.
const flippedSvgStyle: CSSProperties = { transform: "scaleY(-1)" };

export function RevealToggle(props: { open: boolean; onToggle: () => void }) {
  const [showBackground, setShowBackground] = useState(false);
  const factor = props.open ? -1 : 1;
  const flipStyles = useSpring({
    config: { ...config.gentle },
    from: { transform: `scaleY(${factor * -1})` },
    to: {
      transform: `scaleY(${factor})`,
    },
  });

  return (
    <button type="button" className="cursor-pointer" onClick={props.onToggle}>
      <animated.div style={flipStyles} className="overflow-hidden">
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="38"
            height="37"
            viewBox="0 0 38 37"
            style={flippedSvgStyle}
            onMouseEnter={() => setShowBackground(true)}
            onMouseLeave={() => setShowBackground(false)}
          >
            <g fill="none" transform="translate(-743 0)">
              <circle
                cx="762"
                cy="18.5"
                r="18.5"
                fill="var(--color-muted)"
                opacity={showBackground ? 1 : 0}
              />
              <path
                fill="var(--color-content-muted)"
                d="M771.272 10L774 12.778l-9.273 9.438.007.006L762.005 25l-.005-.007-.005.007-2.729-2.778.006-.006L750 12.778 752.728 10 762 19.439 771.272 10z"
                transform="rotate(180 762 17.5)"
              />
            </g>
          </svg>
        </div>
      </animated.div>
    </button>
  );
}

function RevealBox(props: RevealBoxProps) {
  const [open, setOpen] = useState(false);
  // The header row can wrap on narrow screens, so the collapsed height is
  // measured rather than the historical fixed 96px.
  const [headerRowRef, headerRowHeight] = useHeight();
  const collapsedHeight = Math.max(96, headerRowHeight);
  const overlayStyle = { height: collapsedHeight - 24 };

  function toggleOpen() {
    setOpen(!open);
  }

  return (
    /* w-full is load-bearing: consumers center these rows with items-center
     * flex columns, where a widthless block shrink-wraps to its content and
     * every card takes a different width. */
    <div data-testid="reveal-box" className="w-full">
      {/* Invisible overlay covering the header strip so the whole collapsed
       * row is clickable, not just the chevron. */}
      <div
        className={`absolute inset-x-0 ${layerClass.raised} cursor-pointer`}
        style={overlayStyle}
        onClick={toggleOpen}
        data-testid="reveal-box-toggle"
        role="button"
        tabIndex={0}
        onKeyDown={function (e) {
          if (e.key === "Enter" || e.key === " ") {
            // preventDefault stops Space from scrolling the page.
            e.preventDefault();
            toggleOpen();
          }
        }}
      ></div>
      <div className="relative mb-4 inline-block w-full max-w-[1152px] rounded-[3px] border border-border-strong px-8 py-0 shadow-[var(--color-shadow-ambient)_0px_3px_12px]">
        <AnimatedContainer_ open={open} collapsedHeight={collapsedHeight}>
          <div className="flex w-full flex-col flex-nowrap items-start">
            <div
              ref={headerRowRef}
              className="flex min-h-24 w-full flex-row items-center justify-between"
            >
              {props.header(open)}

              <RevealToggle open={open} onToggle={() => setOpen(!open)} />
            </div>
            <div className="h-8" />
            <SlideDown show={open}>
              <div className="h-full min-h-[750px] w-full pb-4">
                {props.body(open)}
              </div>
            </SlideDown>
            {props.footer && (
              <div className="mb-4 flex w-full flex-row items-center justify-end">
                {props.footer}
              </div>
            )}
          </div>
        </AnimatedContainer_>
      </div>
    </div>
  );
}
export default RevealBox;
